import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GamepadIcon } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useNintendoQuestSummary } from '../../hooks/useNintendoQuestSummary';

// Theme system imports
import { NintendoQuestThemeProvider } from './nintendo-quest/contexts/NintendoQuestThemeContext';
import NintendoQuestThemeOrchestrator from './nintendo-quest/components/NintendoQuestThemeOrchestrator';

export default function RachitNintendoQuest() {
  const { data, loading, error } = useNintendoQuestSummary();

  // Override the manifest when component mounts
  useEffect(() => {
    // Remove existing manifest link
    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) {
      existingManifest.remove();
    }

    // Add Nintendo-specific manifest
    const nintendoManifest = document.createElement('link');
    nintendoManifest.rel = 'manifest';
    nintendoManifest.href = '/nintendo-manifest.json';
    document.head.appendChild(nintendoManifest);

    // Cleanup when component unmounts
    return () => {
      const nintendoManifestLink = document.querySelector('link[href="/nintendo-manifest.json"]');
      if (nintendoManifestLink) {
        nintendoManifestLink.remove();
      }
      
      // Restore main manifest
      const mainManifest = document.createElement('link');
      mainManifest.rel = 'manifest';
      mainManifest.href = '/manifest.json';
      document.head.appendChild(mainManifest);
    };
  }, []);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>🎮 Rachit's Nintendo Switch Quest - Loading...</title>
          <meta name="theme-color" content="#e60012" />
          <link rel="icon" href="/nintendo-icon-32.png" sizes="32x32" />
          <link rel="apple-touch-icon" href="/nintendo-icon-180.png" />
        </Helmet>
        
        <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-blue-800">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <GamepadIcon className="w-16 h-16 text-white/80 mx-auto mb-4 animate-pulse" />
                <h2 className="text-2xl font-bold text-white mb-2">Loading Quest Data...</h2>
                <p className="text-white/70">Fetching your Nintendo Switch progress</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>🎮 Nintendo Quest - Error</title>
          <meta name="theme-color" content="#e60012" />
          <link rel="icon" href="/nintendo-icon-32.png" sizes="32x32" />
          <link rel="apple-touch-icon" href="/nintendo-icon-180.png" />
        </Helmet>
        
        <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-blue-800">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <GamepadIcon className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Error Loading Quest</h2>
                <p className="text-white/70">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!data) return null;

  return (
    <>
      <Helmet>
        <title>🎮 Rachit's Nintendo Switch Quest</title>
        <meta name="description" content="Track Rachit's progress toward earning a Nintendo Switch through gamified learning activities. Current progress and achievements dashboard." />
        <meta name="theme-color" content="#e60012" />
        
        {/* Nintendo-specific icons */}
        <link rel="icon" href="/nintendo-icon-32.png" sizes="32x32" />
        <link rel="icon" href="/nintendo-icon-192.png" sizes="192x192" />
        <link rel="apple-touch-icon" href="/nintendo-icon-180.png" />
        
        {/* Override app title for iOS */}
        <meta name="apple-mobile-web-app-title" content="Nintendo Quest" />
        <meta name="application-name" content="Nintendo Quest" />
        
        {/* Open Graph for sharing */}
        <meta property="og:title" content="🎮 Rachit's Nintendo Switch Quest" />
        <meta property="og:description" content="Gaming-style dashboard tracking progress toward earning a Nintendo Switch!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chintantank.info/tools/rachit-nintendo" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="🎮 Rachit's Nintendo Switch Quest" />
        <meta name="twitter:description" content="Gaming-style dashboard tracking progress toward earning a Nintendo Switch!" />
      </Helmet>
      
      <NintendoQuestThemeProvider>
        <NintendoQuestThemeOrchestrator data={data} />
      </NintendoQuestThemeProvider>
    </>
  );
}